/**
 * Collection filtering and sorting, without full page reloads.
 *
 * The markup is a real <form> wrapping real inputs and real links, so filtering
 * works with JS disabled — the form submits, the server renders. This upgrades
 * that: it fetches the same URL, swaps in the new results and facets, and keeps
 * the address bar in step so filtered views stay shareable and the back button
 * behaves.
 *
 * Requests are sequenced: a slow early response can never overwrite a newer one.
 */
const RESULTS = '[data-collection-results]';
const FACETS = '[data-collection-facets]';
const ACTIVE = '[data-active-facets]';
const DEBOUNCE_MS = 350;

class CollectionFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.controller = null;
    this.debounce = null;
    this.requestId = 0;

    this.onChange = this.onChange.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    document.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    document.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    this.controller?.abort();
    clearTimeout(this.debounce);
  }

  onChange(event) {
    // Price is typed, so it is handled by onInput's debounce instead.
    if (event.target.type === 'number') return;
    this.apply(this.urlFromForm());
  }

  onInput(event) {
    if (event.target.type !== 'number') return;
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.apply(this.urlFromForm()), DEBOUNCE_MS);
  }

  /** Pagination and "clear" links live outside the form. */
  onClick(event) {
    const link = event.target.closest('[data-facet-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    this.apply(link.href);
  }

  onPopState() {
    this.apply(window.location.href, { push: false });
  }

  urlFromForm() {
    const params = new URLSearchParams(new FormData(this.form));
    // Empty price boxes would otherwise send filter.v.price.gte= and match nothing.
    for (const [key, value] of [...params]) {
      if (value === '') params.delete(key);
    }
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  async apply(url, { push = true } = {}) {
    const results = document.querySelector(RESULTS);
    results?.setAttribute('aria-busy', 'true');

    this.controller?.abort();
    this.controller = new AbortController();
    const id = ++this.requestId;

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const markup = await response.text();

      // A response that arrived late must not clobber a newer one.
      if (id !== this.requestId) return;

      const fresh = new DOMParser().parseFromString(markup, 'text/html');
      this.swap(fresh, RESULTS);
      this.swap(fresh, FACETS);
      this.swap(fresh, ACTIVE);

      if (push) history.pushState({}, '', url);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to a normal navigation rather than leaving a stale grid.
      window.location.href = url;
    } finally {
      if (id === this.requestId) results?.removeAttribute('aria-busy');
    }
  }

  swap(fresh, selector) {
    const incoming = fresh.querySelector(selector);
    const current = document.querySelector(selector);
    if (!incoming || !current) return;

    // Preserve which filter panels the visitor had open.
    const open = new Set(
      [...current.querySelectorAll('details[open]')].map((d) => d.dataset.facetKey)
    );
    current.innerHTML = incoming.innerHTML;

    // Swapped-in cards arrive with .reveal, which is opacity 0 until the
    // observer in reveal.js reaches them — and that observer scanned the page
    // once, at load. Nothing re-scans, so a filtered grid rendered invisible
    // and sorting looked broken. Marking them visible outright is also the
    // right behaviour: this content replaces what the visitor is already
    // looking at, so it should not fade in from nothing.
    for (const el of current.querySelectorAll('.reveal')) el.classList.add('is-visible');

    // And the same trap again for the pictures, one layer down. base.css holds
    // `img[loading="lazy"]` at opacity 0 until lazy-image.js marks it loaded —
    // and that script, like reveal.js, only ever watched the images the page
    // was born with. Swapped-in cards came back pictureless.
    //
    // Dropping to eager is the fix rather than re-running the watcher, because
    // it settles both halves at once: the selector stops matching, so there is
    // nothing to fade in, and the image is fetched now instead of when the
    // browser decides it is near enough to the viewport. This grid is the
    // thing the visitor just asked for.
    for (const img of current.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';

    // `hidden` sits on the container, not its contents, so innerHTML alone
    // would leave the active-filter bar visible when it should collapse.
    current.toggleAttribute('hidden', incoming.hasAttribute('hidden'));

    for (const details of current.querySelectorAll('details')) {
      if (open.has(details.dataset.facetKey)) details.open = true;
    }
  }
}

customElements.define('collection-facets', CollectionFacets);
